import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, PureComponent } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles'
import { detectActivationEvent, generateUniqueId } from '../../utils/helpers';
import { withRtl } from '../hoc';

import style from './Tooltip.scss';

class Tooltip extends PureComponent {
  static propTypes = {
    // icon: PropTypes.oneOfType([PropTypes.bool, PropTypes.node, PropTypes.string]).isRequired,
    icon: PropTypes.node.isRequired,
    children: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
    label: PropTypes.string,
    ariaLabel: PropTypes.string,
    isAriaPressed: PropTypes.shape({
      active: PropTypes.bool,
      value: PropTypes.bool,
    }),
    // data: PropTypes.string,
    large: PropTypes.bool,
    // left: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
    left: PropTypes.number,
    top: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
    width: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    click: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    verticalPosition: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    simulateClick: PropTypes.bool,
    onMouseLeave: PropTypes.func,
    onMouseEnter: PropTypes.func,
  };

  static defaultProps = {
    children: false,
    label: '',
    ariaLabel: '',
    isAriaPressed: { active: false, value: false },
    large: false,
    left: 0,
    top: false,
    width: false,
    click: false,
    verticalPosition: false,
    simulateClick: false,
    onMouseLeave: null,
    onMouseEnter: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      left: 0,
      open: false,
      top: 0,
      uniqueId: generateUniqueId({ label: props.ariaLabel || props.label }),
      initialLoad: true
    };
  }

  componentDidUpdate(nextProps) {
    if (nextProps.simulateClick !== this.props.simulateClick) {
      this.$btn.click();
    }
  }

  handleMouseLeave = () => {
    const { onMouseLeave, simulateClick } = this.props;
    window.removeEventListener('scroll', this.handleMouseLeave);
    this.setState({
      ...this.state,
      open: false,
    });

    if (onMouseLeave && simulateClick) {
      onMouseLeave();
    }
  };

  handleMouseEnter = event => {
    const { onMouseEnter, applyMobileWidth } = this.props;
    // sk: no more target, do we like using refs?
    // const target = event.currentTarget;
    const target = this.$btn;

    const targetRect = target.getBoundingClientRect();
    const tooltipRect = target.nextSibling.getBoundingClientRect();
    // sk: haaack-o-rama
    const yPos = event.screenY - (51 + 62); // header?
    let vPos = 'center';
    if (yPos <= window.innerHeight * 0.35) {
      vPos = 'top';
    } else if (yPos >= window.innerHeight * 0.65) {
      vPos = 'bottom';
    }

    let left = this.props.left || target.offsetWidth + 18;
    let top = this.props.top || target.offsetHeight * 0.5;
    if(this.props.is_last_school){
      vPos = 'bottom';
    }
    // we are not on screen 100% based on width and position of the target
    if (!this.props.left && targetRect.right + 18 + tooltipRect.width > window.innerWidth) {
      // reverse position
      left = -tooltipRect.width - targetRect.width;
      if(applyMobileWidth){
        left += 38;
      }
    }

    // Tooltip vertical postion can be forced with `verticalPosition` prop
    if (this.props.verticalPosition) {
      vPos = this.props.verticalPosition;
    }

    window.addEventListener('scroll', this.handleMouseLeave);
    this.setState({
      ...this.state,
      left,
      top,
      vPos,
      open: true,
    });

    if (onMouseEnter) {
      onMouseEnter();
    }
  };

  toggle = event => {
    const { open } = this.state;
    if(this.state.initialLoad){
      event.preventDefault();
      event.stopPropagation();
      this.handleMouseEnter(event);
      this.setState({
        ...this.state,
        initialLoad: false
      });
    } else {
      if (!open && detectActivationEvent(event)) {
        event.preventDefault();
        event.stopPropagation();
        this.handleMouseEnter(event);
      } else if (open && detectActivationEvent(event)) {
        event.preventDefault();
        event.stopPropagation();
        this.handleMouseLeave(event);
      }
    }
  };

  iconLabel = () => {
    const { icon, label, ariaLabel } = this.props;
    return (
      <Fragment>
        <span className={style.label}>
          {icon}
          <span className="visually-hidden">{ariaLabel || label}</span>
          {label && (
            <span aria-hidden className={style.label}>
              {label}
            </span>
          )}
        </span>
      </Fragment>
    );
  };

  render() {
    // TODO: Make optional argument additions more elegant
    const { children, large, icon, label, width, click, ariaLabel, isAriaPressed, applyMobileWidth } = this.props;
    const { left, open, top, vPos, uniqueId } = this.state;
    const bodyClass = classNames(style.body, { [style.bodyLarge]: large });
    const tipClass = classNames(style.root, {
      [style.on]: open,
      [style.left]: left < 0,
      [style.top]: vPos === 'top',
      [style.bottom]: vPos === 'bottom',
      [style.center]: vPos === 'center',
    });
    const tipStyle = { left, top };
    const isAriaPressedRequired = isAriaPressed.active;

    const iconLabelElement = (
      <span className={style.label}>
        {icon}
        {label}
      </span>
    );

    if (width) {
      tipStyle.width = width;
    }

    if(applyMobileWidth){
      tipStyle.width = applyMobileWidth;
    }

    if (!children) {
      return this.iconLabel();
    }

    return (
      <Fragment>
        <div aria-label={label} className={style.tipContainer}>
          {click &&
            isAriaPressedRequired && (
              <div
                ref={btn => {
                  this.$btn = btn;
                }}
                role="button"
                aria-label={ariaLabel || label}
                className={style.button}
                onClick={this.toggle}
                onKeyDown={event => this.toggle(event)}
                aria-pressed={isAriaPressed.value}
                tabIndex="0"
                aria-labelledby={uniqueId}
              >
                {iconLabelElement}
              </div>
            )}
          {click &&
            !isAriaPressedRequired && (
              <div
                ref={btn => {
                  this.$btn = btn;
                }}
                role="button"
                aria-label={ariaLabel || label}
                className={style.button}
                onClick={this.toggle}
                onKeyDown={event => this.toggle(event)}
                tabIndex="0"
                aria-labelledby={uniqueId}
              >
                {iconLabelElement}
              </div>
            )}
          {!click &&
            isAriaPressedRequired && (
              <div
                ref={btn => {
                  this.$btn = btn;
                }}
                role="button"
                aria-label={ariaLabel || label}
                className={style.button}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                onKeyDown={event => this.toggle(event)}
                onClick={event => this.toggle(event)}
                tabIndex="0"
                aria-labelledby={uniqueId}
              >
                {iconLabelElement}
              </div>
            )}
          {!click &&
            !isAriaPressedRequired && (
              <div
                ref={btn => {
                  this.$btn = btn;
                }}
                role="button"
                aria-label={ariaLabel || label}
                aria-pressed={isAriaPressed.value}
                className={style.button}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                onKeyDown={event => this.toggle(event)}
                onClick={event => this.toggle(event)}
                tabIndex="0"
                aria-labelledby={uniqueId}
              >
                {iconLabelElement}
              </div>
            )}
          <div 
            onMouseEnter={this.props.keepTooltipOpen ? this.handleMouseEnter : null}
            onMouseLeave={this.handleMouseLeave}
            id={uniqueId} className={tipClass} role="tooltip" style={tipStyle} aria-hidden={!open}>
            <div className={bodyClass}>{children}</div>
          </div>
        </div>
      </Fragment>
    );
  }
}

export default withRtl(style)(withStyles(style)(Tooltip));
